// Lightweight i18n for the command-bus UI. Labels are keyed by the device
// command `name` (stable, language-neutral) -- NOT by the catalog `label`,
// which the momi sends in a single language and we only use as a fallback.
//
// When the app adopts a full i18n framework (e.g. react-i18next), move these
// dictionaries into the locale JSON files and replace `useLocale()` with the
// framework's current-language hook; the call sites stay identical.

export type Locale = 'fr' | 'en'

export type CategoryId =
  | 'relais'
  | 'lecture'
  | 'ecriture'
  | 'controle'
  | 'configuration'
  | 'maintenance'
  | 'autres'

// Current UI locale -- a plain module variable, no framework. Defaults to 'fr'.
// When the site goes English, call `setLocale('en')` once at startup (or wire
// it to whatever language source you add later). Read via `getLocale()`.
let currentLocale: Locale = 'fr'

export function getLocale(): Locale {
  return currentLocale
}

export function setLocale(locale: Locale): void {
  currentLocale = locale
}

// ── Category labels ─────────────────────────────────────────────────────────

export const CATEGORY_LABELS: Record<Locale, Record<CategoryId, string>> = {
  fr: {
    relais: 'Relais',
    lecture: 'Lecture',
    ecriture: 'Écriture',
    controle: 'Contrôle',
    configuration: 'Configuration',
    maintenance: 'Maintenance',
    autres: 'Paramètres/Autres',
  },
  en: {
    relais: 'Relay',
    lecture: 'Read',
    ecriture: 'Write',
    controle: 'Control',
    configuration: 'Configuration',
    maintenance: 'Maintenance',
    autres: 'Settings/Other',
  },
}

export function categoryLabel(id: CategoryId, locale: Locale): string {
  return CATEGORY_LABELS[locale]?.[id] ?? CATEGORY_LABELS.fr[id] ?? id
}

// ── Command labels (keyed by command name) ──────────────────────────────────

export const COMMAND_LABELS: Record<Locale, Record<string, string>> = {
  fr: {
    open_relay: 'Ouvrir le relais',
    close_relay: 'Fermer le relais',
    read_relay: "Lire l'état du relais",
    send_token: 'Envoyer un token',
    send_at_command: 'Envoyer une commande AT',
    time_sync: "Synchroniser l'heure",
    read_time: "Lire l'heure",
    write_time: "Régler l'heure",
    read_date: 'Lire la date',
    write_date: 'Régler la date',
    read_lorawan_parameters: 'Lire les paramètres LoRaWAN',
    read_appkey: "Lire l'AppKey",
    write_appkey: "Modifier l'AppKey",
    read_frq_plan: 'Lire le plan de fréquences',
    write_frq_plan: 'Modifier le plan de fréquences',
    read_report_interval: "Lire l'intervalle de report",
    write_report_interval: "Régler l'intervalle de report",
    read_protocol_version: 'Lire la version du protocole',
    read_process_status: "Lire l'état du compteur",
    read_online_status: "Lire l'état de connexion",
    read_signal_strength: 'Lire la puissance du signal (RSSI)',
    read_snr: 'Lire le rapport signal/bruit (SNR)',
    read_report_objects_1: 'Lire les objets de report 1',
    read_report_objects_2: 'Lire les objets de report 2',
    read_reported_data: 'Lire les données reportées',
    read_total_kwh: "Lire l'énergie totale (kWh)",
    read_kwh_specific_month: "Lire l'énergie d'un mois donné",
    read_kwh_last_day: "Lire l'énergie du dernier jour",
    read_voltage: 'Lire la tension',
    read_current: 'Lire le courant',
    read_active_power: 'Lire la puissance active',
    read_frequency: 'Lire la fréquence',
    read_total_power_factor: 'Lire le facteur de puissance',
    read_voltage_phase_b: 'Lire la tension (phase B)',
    read_voltage_phase_c: 'Lire la tension (phase C)',
    read_current_phase_b: 'Lire le courant (phase B)',
    read_current_phase_c: 'Lire le courant (phase C)',
    read_active_power_phase_a: 'Lire la puissance active (phase A)',
    read_active_power_phase_b: 'Lire la puissance active (phase B)',
    read_active_power_phase_c: 'Lire la puissance active (phase C)',
    read_power_factor_phase_a: 'Lire le facteur de puissance (phase A)',
    read_power_factor_phase_b: 'Lire le facteur de puissance (phase B)',
    read_power_factor_phase_c: 'Lire le facteur de puissance (phase C)',
    read_credit: 'Lire le crédit restant',
    read_total_buy_amount: 'Lire le montant total acheté',
    read_max_power_limit: 'Lire la limite de puissance max',
    read_last_credit_amount: 'Lire le dernier crédit (montant)',
    read_last_credit_token: 'Lire le dernier token de crédit',
    read_open_tail_cover_count: "Lire le nombre d'ouvertures du capot",
    read_open_tail_cover_time: "Lire l'heure d'ouverture du capot",
    read_power_down_count: 'Lire le nombre de coupures',
    read_power_down_time: 'Lire l\'heure de la dernière coupure',
    read_status_flags: "Lire les indicateurs d'état",
    read_fraud_status: "Lire l'état de fraude",
    // zhaga / luminaire (UL20xx)
    on: 'Allumer',
    off: 'Éteindre',
    dimming: 'Gradation',
    dim: 'Gradation manuelle',
    dim_resume: 'Reprendre le profil de gradation',
  },
  en: {
    open_relay: 'Open relay',
    close_relay: 'Close relay',
    read_relay: 'Read relay status',
    send_token: 'Send token',
    send_at_command: 'Send AT command',
    time_sync: 'Sync time',
    read_time: 'Read time',
    write_time: 'Set time',
    read_date: 'Read date',
    write_date: 'Set date',
    read_lorawan_parameters: 'Read LoRaWAN parameters',
    read_appkey: 'Read AppKey',
    write_appkey: 'Set AppKey',
    read_frq_plan: 'Read frequency plan',
    write_frq_plan: 'Set frequency plan',
    read_report_interval: 'Read report interval',
    write_report_interval: 'Set report interval',
    read_protocol_version: 'Read protocol version',
    read_process_status: 'Read meter status',
    read_online_status: 'Read online status',
    read_signal_strength: 'Read signal strength (RSSI)',
    read_snr: 'Read SNR',
    read_report_objects_1: 'Read report objects 1',
    read_report_objects_2: 'Read report objects 2',
    read_reported_data: 'Read reported data',
    read_total_kwh: 'Read total energy (kWh)',
    read_kwh_specific_month: 'Read energy for a given month',
    read_kwh_last_day: 'Read last day energy',
    read_voltage: 'Read voltage',
    read_current: 'Read current',
    read_active_power: 'Read active power',
    read_frequency: 'Read frequency',
    read_total_power_factor: 'Read power factor',
    read_voltage_phase_b: 'Read voltage (phase B)',
    read_voltage_phase_c: 'Read voltage (phase C)',
    read_current_phase_b: 'Read current (phase B)',
    read_current_phase_c: 'Read current (phase C)',
    read_active_power_phase_a: 'Read active power (phase A)',
    read_active_power_phase_b: 'Read active power (phase B)',
    read_active_power_phase_c: 'Read active power (phase C)',
    read_power_factor_phase_a: 'Read power factor (phase A)',
    read_power_factor_phase_b: 'Read power factor (phase B)',
    read_power_factor_phase_c: 'Read power factor (phase C)',
    read_credit: 'Read remaining credit',
    read_total_buy_amount: 'Read total purchased amount',
    read_max_power_limit: 'Read max power limit',
    read_last_credit_amount: 'Read last credit amount',
    read_last_credit_token: 'Read last credit token',
    read_open_tail_cover_count: 'Read tail cover open count',
    read_open_tail_cover_time: 'Read tail cover open time',
    read_power_down_count: 'Read power-down count',
    read_power_down_time: 'Read last power-down time',
    read_status_flags: 'Read status flags',
    read_fraud_status: 'Read fraud status',
    // zhaga / luminaire (UL20xx)
    on: 'Turn on',
    off: 'Turn off',
    dimming: 'Dimming',
    dim: 'Manual dimming',
    dim_resume: 'Resume dimming profile',
  },
}

// Translate a command name. Falls back to the catalog-provided label (the
// momi's single-language default), then to the raw name.
export function commandLabel(
  name: string,
  locale: Locale,
  fallback?: string,
): string {
  return COMMAND_LABELS[locale]?.[name] ?? fallback ?? name
}

// ── Static modal UI strings ─────────────────────────────────────────────────

export interface CommandBusUiStrings {
  tabCommands: string
  tabHistory: string
  momiOnline: string
  momiOffline: string
  destructiveTag: string
  send: string
  sending: string
  cancel: string
  confirm: string
  confirmTitle: string
  confirmBody: (label: string) => string
  loadingCatalog: string
  catalogError: string
  notProvisioned: string
  noCommands: string
}

export const UI: Record<Locale, CommandBusUiStrings> = {
  fr: {
    tabCommands: 'Commandes',
    tabHistory: 'Historique',
    momiOnline: 'MoMi en ligne',
    momiOffline: 'Momi hors ligne',
    destructiveTag: 'destructif',
    send: 'Envoyer',
    sending: 'Envoi…',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    confirmTitle: 'Confirmer la commande',
    confirmBody: (label) =>
      `Cette commande est marquée comme destructive. Confirmer l'envoi de « ${label} » au device ?`,
    loadingCatalog: 'Chargement du catalogue…',
    catalogError: 'Impossible de récupérer le catalogue des commandes.',
    notProvisioned: 'Device non provisionné dans le command bus.',
    noCommands: 'Aucune commande disponible pour cette famille de device.',
  },
  en: {
    tabCommands: 'Commands',
    tabHistory: 'History',
    momiOnline: 'MoMi online',
    momiOffline: 'MoMi offline',
    destructiveTag: 'destructive',
    send: 'Send',
    sending: 'Sending…',
    cancel: 'Cancel',
    confirm: 'Confirm',
    confirmTitle: 'Confirm command',
    confirmBody: (label) =>
      `This command is marked as destructive. Send “${label}” to the device?`,
    loadingCatalog: 'Loading catalog…',
    catalogError: 'Could not fetch the command catalog.',
    notProvisioned: 'Device not provisioned in the command bus.',
    noCommands: 'No commands available for this device family.',
  },
}

export function uiStrings(locale: Locale): CommandBusUiStrings {
  return UI[locale] ?? UI.fr
}
